import { formatDurationBetween, formatDurationMs } from '~/utils/date';

import type { LineageItem, Stage } from '../types/lineage-images.api.types';
import type { RunProgress } from '../types/run.api.types';

import { stageProgressOf } from './product-history';

/** One stage row's left-hand "when" and right-hand "duration", for the Journey timeline (S2). */
export interface StageTiming {
  /** When this product's own work on the stage began — a ledger item's `createdAt` for a ledger
   *  stage, `StageProgress.startedAt` otherwise. `null` while the stage hasn't started. */
  startedAt: string | null;
  /** Pre-formatted compact span ("1s", "10m 14s", "1d 2h"); `null` when it can't be computed yet. */
  durationText: string | null;
  /** True when `durationText` is "time elapsed so far" (a still-open gate/stage) rather than a
   *  finished span — callers can use this to prefix it ("waiting …") instead of showing it bare. */
  isOngoing: boolean;
}

/**
 * This product's own earliest ledger item for one stage, across every market/attempt — "when the
 * stage started" for IMPORT / IMAGE_GATE / CURATION (design: NAMES_AND_HISTORY_APPROVED.md /
 * CLARITY_BATCH_BRIEF.md S2 — "product's own attempt time for ledger stages").
 */
function earliestLedgerTime(items: LineageItem[], stage: Stage): string | null {
  const times = items.filter((i) => i.stage === stage).map((i) => i.createdAt);
  if (times.length === 0) return null;
  return times.reduce((min, t) => (t < min ? t : min));
}

/**
 * Builds the left-hand "started" time and right-hand duration for every stage of the Journey
 * timeline, in flow order. A stage's duration prefers its own authoritative figure
 * (`StageProgress.completedAt`/`elapsedMs`, or the open gate's `waitingMs` while it's the one
 * waiting on the user); a ledger stage — which carries no duration of its own — falls back to the
 * gap until the next stage (ledger or content) that has a start time, so "how long Matching took"
 * reads as "until Score/Sample picked the product up", matching the mock in CLARITY_BATCH_BRIEF.md.
 */
export function buildStageTimings(
  stages: readonly Stage[],
  runItems: LineageItem[],
  progress: RunProgress | undefined,
): Map<Stage, StageTiming> {
  const startedAt = new Map<Stage, string | null>();
  for (const stage of stages) {
    const ledgerStart = earliestLedgerTime(runItems, stage);
    startedAt.set(stage, ledgerStart ?? stageProgressOf(progress, stage)?.startedAt ?? null);
  }

  const timings = new Map<Stage, StageTiming>();
  stages.forEach((stage, index) => {
    const start = startedAt.get(stage) ?? null;
    if (!start) {
      timings.set(stage, { startedAt: null, durationText: null, isOngoing: false });
      return;
    }
    const sp = stageProgressOf(progress, stage);
    if (sp?.elapsedMs != null) {
      timings.set(stage, {
        startedAt: start,
        durationText: formatDurationMs(sp.elapsedMs) || null,
        isOngoing: false,
      });
      return;
    }
    if (sp?.completedAt) {
      timings.set(stage, {
        startedAt: start,
        durationText: formatDurationBetween(start, sp.completedAt) || null,
        isOngoing: false,
      });
      return;
    }
    // No completion yet — the gap until the next stage that has started is the honest duration
    // for a ledger stage already finished in practice (its own items never carry a finish time).
    const nextStart = stages
      .slice(index + 1)
      .map((s) => startedAt.get(s) ?? null)
      .find((t): t is string => !!t);
    if (nextStart) {
      timings.set(stage, {
        startedAt: start,
        durationText: formatDurationBetween(start, nextStart) || null,
        isOngoing: false,
      });
      return;
    }
    // This is the frontier stage with nothing after it yet — an open gate's own `waitingMs` is the
    // one authoritative "how long has this been open" figure; otherwise leave the duration blank
    // rather than guess at one from `start` to "now" (which would need a re-render tick to stay live).
    const waitingMs =
      progress?.pendingGate?.stage === stage ? (progress.pendingGate.waitingMs ?? null) : null;
    timings.set(stage, {
      startedAt: start,
      durationText: waitingMs != null ? formatDurationMs(waitingMs) || null : null,
      isOngoing: waitingMs != null,
    });
  });
  return timings;
}
